import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Divider,
  IconButton,
  Button,
  Alert,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Payment as PaymentIcon,
  Image as ImageIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  MarkEmailRead as MarkEmailReadIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { purchaseOrderService } from '../services/purchaseOrderService';
// ⚠️ SEUL `libelleTypeReglement` RESTE : la LISTE des codes et la valeur par
// défaut servaient le formulaire de paiement, qui a quitté ce fichier au lot
// RU4-d. Ce panneau ne fait plus que LIRE le type d'un paiement enregistré.
import { libelleTypeReglement } from '../constants/paymentType';
import { useLanguage } from '../contexts/LanguageContext';
import { cle, useMessageTraduit } from '../utils/messageTraduit';
import PastilleStatut from './commun/PastilleStatut';
import { useFormatageDates } from '../utils/dates';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import ReceiptFormDialog from './ReceiptFormDialog';
import DialogueAjoutPaiement from './commandes/DialogueAjoutPaiement';
import DocumentViewerModal, { typeDuFichier } from './DocumentViewerModal';
import DialogueConfirmation from './commun/DialogueConfirmation';
import { ORDER_STATUS, isCancellable, canReceive } from '../constants/orderStatus';
import { derivePaymentStatus } from '../constants/paymentStatus';
import { numeroAffichable } from '../utils/numeroCommande';

// ⚠️ LES FILETS DU BANDEAU SONT DES BORDURES, PAS DES `<Divider>` : une grille
// MUI retombe à la ligne sous `md`, et un filet vertical posé entre deux cases
// resterait alors seul au milieu de rien. `borderLeft` appartient à la case ;
// elle l'emmène avec elle quand elle passe à la ligne — et `borderTop` prend le
// relais sur petit écran, où la séparation devient horizontale.
const CASE = { p: 2.5 };
const FILET = {
  borderTop: { xs: 1, md: 0 },
  borderLeft: { xs: 0, md: 1 },
  borderColor: { xs: 'divider', md: 'divider' },
};

// Un fait de la ligne de statut : le libellé À CÔTÉ de la valeur, jamais
// au-dessus. Empilés, trois faits prennent deux niveaux et forcent l'œil à
// descendre puis remonter ; côte à côte ils se lisent d'un trait.
const fait = (libelle, valeur) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
    <Typography variant="caption" color="text.secondary">{libelle}</Typography>
    <Typography variant="body2" fontWeight="medium">{valeur}</Typography>
  </Box>
);

// `contexte` : le libellé du PARENT dans le fil d'Ariane. Optionnel — le panneau
// s'ouvre depuis Commandes, Réception et Comptabilité, et il ne peut pas deviner
// d'où. Sans lui, le parent est « Commandes ».
/**
 * La couleur de chaque état de reçu.
 *
 * 🔴 UNE TABLE PLUTÔT QU'UNE TERNAIRE. L'ancienne écriture disait
 * `status === 'empty' ? 'default' : 'warning'` : **elle n'avait que deux
 * issues pour trois états**, exactement comme la fonction qu'elle habillait.
 * Un troisième état y serait entré en ambre — la couleur de l'alerte — alors
 * qu'il est la bonne nouvelle. *Une ternaire suit le nombre de cas qu'elle
 * connaissait le jour où on l'a écrite.*
 */
const COULEUR_ETAT_RECU = {
  empty: 'default',
  partial: 'warning',
  complete: 'success',
};

const OrderDetailView = ({ order, showSnackbar, onOrderUpdated, contexte }) => {
  const { t } = useLanguage();
  // ⚠️ DEUX FAMILLES SUR CE SEUL ÉCRAN : `order_date` / `payment_date` sont des
  // jours civils (jamais convertis), `receipt.created_at` est un instant.
  const { formaterHorodatage, formaterDateSaisie } = useFormatageDates();
  const currencySymbol = getCurrencySymbol(order.currency || 'EUR');
  const [receipts, setReceipts] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [error, setError] = useMessageTraduit();
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentError, setPaymentError] = useMessageTraduit();
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  /**
   * 🔴 LE FICHIER REGARDÉ — UN SEUL ÉTAT POUR LES DEUX SURFACES QUI OUVRAIENT
   * UN ONGLET : la pièce jointe d'un reçu et la photo de facture d'un paiement.
   * ⚠️ *Deux états auraient permis d'ouvrir les deux à la fois* — et l'un
   * serait passé sous l'autre sans que rien ne le dise.
   * Porte `{ chemin, titre, recuId }` — `recuId` n'est renseigné que pour une
   * pièce jointe de REÇU : c'est ce qui décide si le bouton « remplacer »
   * s'affiche, sans que le visualiseur ait à connaître le métier.
   */
  /**
   * 🔴 L'ANNONCE AVANT UN GESTE IRRÉVERSIBLE — LES TROIS DE CET ÉCRAN TOUCHENT
   * LA COMPTABILITÉ, et c'est ce qui en faisait la dette la plus urgente des
   * huit `window.confirm` restants.
   *
   * ⚠️ UN SEUL ÉTAT POUR LES TROIS : deux annonces ne peuvent pas être
   * ouvertes en même temps, et un état par geste aurait permis de les
   * superposer. Il porte `{ type, executer, ...contenu }` — le contenu est
   * calculé au MOMENT DU CLIC, sur les données que l'écran a déjà.
   *
   * 🔴 ET LES CONSÉQUENCES SONT MESURÉES, PAS RÉDIGÉES. *« Supprimer ce reçu »
   * n'apprend rien ; « 3 produits vont sortir du stock » décide.* On n'annonce
   * que ce qu'on peut compter — leçon RU7-c-3, et le lot RU7-c-3 avait failli
   * annoncer un compteur que le serveur ne servait pas.
   */
  const [aConfirmer, setAConfirmer] = useState(null);
  const [confirmationEnCours, setConfirmationEnCours] = useState(false);

  const executerConfirmation = async () => {
    if (!aConfirmer?.executer) return;
    setConfirmationEnCours(true);
    try {
      await aConfirmer.executer();
    } finally {
      setConfirmationEnCours(false);
      setAConfirmer(null);
    }
  };

  const [fichierRegarde, setFichierRegarde] = useState(null);
  const [remplacementEnCours, setRemplacementEnCours] = useState(false);
  const [erreurRemplacement, setErreurRemplacement] = useState(null);

  /**
   * Remplace la pièce jointe d'un reçu.
   *
   * 🔴 LA ROUTE ET LE SERVICE EXISTAIENT DÉJÀ — `PUT …/receipts/<id>` remplace
   * le fichier ET supprime l'ancien, `updateReceiptAttachment` l'appelle — et
   * **AUCUN écran ne s'en servait**. Mesuré au lot C : route morte côté
   * interface. *Le travail était fait aux deux bouts ; il manquait le bouton.*
   */
  const remplacerPieceJointe = async (evenement) => {
    const fichier = evenement.target.files[0];
    if (!fichier || !fichierRegarde?.recuId) return;
    evenement.target.value = '';        // rechoisir le même fichier doit émettre
    setErreurRemplacement(null);
    setRemplacementEnCours(true);
    try {
      const maj = await purchaseOrderService.updateReceiptAttachment(
        order.id, fichierRegarde.recuId, fichier,
      );
      // ⚠️ LE VISUALISEUR SUIT LE NOUVEAU CHEMIN : garder l'ancien afficherait
      // le fichier remplacé, donc prouverait que rien n'a changé — le défaut
      // exact que le lot D vient de corriger sur la photo produit.
      const chemin = maj?.attachment_path || maj?.receipt?.attachment_path;
      if (chemin) setFichierRegarde((f) => ({ ...f, chemin }));
      loadReceipts();
    } catch (err) {
      setErreurRemplacement(err?.message || t('common.error'));
    } finally {
      setRemplacementEnCours(false);
    }
  };
  const [ongletActif, setOngletActif] = useState(0);

  const loadReceipts = async () => {
    if (!order) return;
    
    try {
      setLoadingReceipts(true);
      setError(null);
      const response = await purchaseOrderService.getOrderReceipts(order.id);
      setReceipts(response.receipts || []);
    } catch (err) {
      console.error('Erreur lors du chargement des reçus:', err);
      setError(cle('orders.loadReceiptsError'));
    } finally {
      setLoadingReceipts(false);
    }
  };

  useEffect(() => {
    if (order) {
      loadReceipts();
      loadPayments();
    }
  }, [order]);

  const loadPayments = async () => {
    if (!order) return;
    
    try {
      setLoadingPayments(true);
      setPaymentError(null);
      const response = await purchaseOrderService.getOrderPayments(order.id);
      setPayments(response.payments || []);
    } catch (err) {
      console.error('Erreur lors du chargement des paiements:', err);
      // ⚠️ LE REPLI `|| 'Erreur…'` EST PARTI AVEC : il était MORT. `t()` rend
      // toujours une chaîne truthy — la clé brute à défaut de traduction —
      // donc la branche de droite n'a jamais pu s'exécuter. *L'un des 73
      // replis décoratifs mesurés au lot RU5-e.*
      setPaymentError(cle('orders.loadPaymentsError'));
    } finally {
      setLoadingPayments(false);
    }
  };

  if (!order) return null;

  const handleCancel = async () => {
    try {
      await purchaseOrderService.cancelOrder(order.id);
      showSnackbar(t('orders.orderCancelled'), 'success');
      onOrderUpdated && onOrderUpdated();
    } catch (error) {
      showSnackbar(t('orders.cancelError'), 'error');
    }
  };

  const handleSendEmail = async () => {
    try {
      const res = await purchaseOrderService.sendPurchaseOrderEmail(order.id);
      if (res.sent) {
        showSnackbar(t('messages.orderSentByEmail'), 'success');
      } else {
        showSnackbar(t('orders.emailSendFailed'), 'error');
      }
      onOrderUpdated && onOrderUpdated();
    } catch (error) {
      showSnackbar(t('orders.sendEmailError'), 'error');
    }
  };

  const handleMarkSent = async () => {
    try {
      await purchaseOrderService.markSent(order.id);
      showSnackbar(t('orders.markedAsSent'), 'success');
      onOrderUpdated && onOrderUpdated();
    } catch (error) {
      showSnackbar(t('orders.markAsSentError'), 'error');
    }
  };

  const handleDeleteReceipt = async (receiptId) => {

    try {
      await purchaseOrderService.deleteOrderReceipt(order.id, receiptId);
      await loadReceipts(); // Recharger la liste
      if (showSnackbar) {
        showSnackbar(t('orders.receiptDeleted'), 'success');
      }
    } catch (err) {
      console.error('Erreur lors de la suppression du reçu:', err);
      if (showSnackbar) {
        showSnackbar(t('orders.receiptDeleteError'), 'error');
      }
    }
  };

  // 🔴 CE PANNEAU GAGNE CE QU'IL N'AVAIT PAS : les trois chiffres et le montant
  // pré-rempli. Ils vivaient dans la copie de la Comptabilité — l'écran
  // spécialisé — pendant que le chemin le plus naturel, celui-ci, restait le
  // plus pauvre. L'extraction ne les a pas ajoutés : elle a cessé de les cacher.
  const handleOpenPaymentDialog = () => setPaymentDialogOpen(true);

  const handleClosePaymentDialog = () => setPaymentDialogOpen(false);

  const enregistrerPaiement = async (charge) => {
    await purchaseOrderService.createOrderPayment(order.id, charge, true);
    await loadPayments();
  };



  const handleDeletePayment = async (paymentId) => {

    try {
      await purchaseOrderService.deleteOrderPayment(order.id, paymentId);
      await loadPayments();
      if (showSnackbar) {
        showSnackbar(t('orders.paymentDeleted') || 'Paiement supprimé avec succès', 'success');
      }
    } catch (err) {
      console.error('Erreur lors de la suppression du paiement:', err);
      if (showSnackbar) {
        showSnackbar(t('orders.paymentDeleteError') || 'Erreur lors de la suppression du paiement', 'error');
      }
    }
  };


  const getTotalPaid = () => {
    return payments.reduce((total, payment) => total + (parseFloat(payment.amount) || 0), 0);
  };

  const getRemainingAmount = () => {
    const totalPayable = parseFloat(order.total_payable || order.total_final) || 0;
    return totalPayable - getTotalPaid();
  };

  const getPaymentStatus = () => {
    const status = derivePaymentStatus(parseFloat(order.total_payable || order.total_final) || 0, getTotalPaid());
    // ⚠️ NE REND PLUS QUE LE CODE — libellé et couleur appartiennent au RENDU,
    // donc à `PastilleStatut`. Ce panneau affichait le statut de paiement à
    // DEUX endroits, et les deux passaient par un `<Chip>`.
    return status;
  };

  // ⚠️ CE PANNEAU EST LE PLUS EXPOSÉ DU LOT, et le moins visible : il n'est
  // pas un écran, il s'ouvre depuis TROIS endroits — Commandes, Réception et
  // Comptabilité. Son `getStatusChip` était recopié à l'identique dans
  // `OrderReception`. Il passe désormais par le seul composant qui peint un
  // statut, comme les trois autres consommateurs.
  const getStatusChip = (status) => <PastilleStatut statut={status} />;

  // Les deux formateurs locaux ont vécu ici jusqu'au lot T3 ; ils sont
  // remplacés par le helper commun (`utils/dates`) — le correctif « par
  // composantes » posé au lot T2 y a été repris tel quel, et il vaut désormais
  // pour les 5 écrans qui affichent une date saisie, pas seulement pour
  // celui-ci. `formatDate` → `formaterDateSaisie`, `formatDateTime` →
  // `formaterHorodatage`.

  // ── L'ACTION PRINCIPALE DU PANNEAU — UNE SEULE, ET ELLE CHANGE AVEC LE STATUT ──
  // 🔴 CE QUI EST EN JEU N'EST PAS L'ESTHÉTIQUE : quatre boutons de même poids
  // obligent à LIRE pour choisir. En désigner un seul comme coloré dit ce qu'on
  // vient faire ici, et le statut est la seule chose qui le détermine.
  // ⚠️ UNE COMMANDE ANNULÉE N'EN A AUCUNE, et c'est délibéré : peindre un bouton
  // qui ne peut rien faire serait pire que ne rien peindre. Le panneau reste
  // consultable — le PDF et les onglets répondent — il n'a simplement plus de
  // geste à proposer. *Un écran sans action est plus honnête qu'une action morte.*
  const actionPrincipale = () => {
    if (order.status === ORDER_STATUS.EN_ATTENTE_ENVOI) {
      return { libelle: order.send_status === 'failed' ? t('orders.resendEmail') : t('orders.sendEmail'),
               icone: <SendIcon />, couleur: 'primary', action: handleSendEmail };
    }
    if (canReceive(order.status)) {
      return { libelle: t('reception.createReceipt'), icone: <AddIcon />,
               couleur: 'success', action: () => setReceiptDialogOpen(true) };
    }
    if (order.status === ORDER_STATUS.LIVRE) {
      return { libelle: t('orders.addPayment'), icone: <PaymentIcon />,
               couleur: 'primary', action: handleOpenPaymentDialog };
    }
    return null;
  };

  // L'onglet Paiements suit l'axe PAIEMENT, qui ne s'ouvre qu'à la livraison :
  // c'est la même règle que la pastille de l'en-tête et que le 409 du serveur.
  const ongletPaiementsVisible = order.status === ORDER_STATUS.LIVRE;

  // 🔴 LE REPLI EST CALCULÉ UNE FOIS, ICI, ET NON RÉPÉTÉ DANS CHAQUE PANNEAU.
  // Sans lui : on consulte les paiements (onglet 2), le statut change — une
  // suppression de reçu ramène la commande à `en_attente_complement` — l'onglet
  // Paiements disparaît, et `ongletActif` vaut toujours 2. Aucun panneau ne
  // correspond : l'écran devient BLANC, sans erreur et sans rien à cliquer.
  // ⚠️ Un index qui survit à la disparition de ce qu'il désigne est un défaut
  // de la même famille que le stock qui survit à son historique : la valeur est
  // intacte, c'est ce qu'elle désignait qui n'existe plus.
  const ongletCourant = (!ongletPaiementsVisible && ongletActif === 2) ? 0 : ongletActif;

  /**
   * L'état d'un reçu : VIDE, PARTIEL, ou COMPLET.
   *
   * 🔴 LA TROISIÈME BRANCHE N'EXISTAIT PAS. La fonction rendait `'partial'`
   * dès que le reçu portait une ligne — **un reçu qui solde la commande était
   * donc étiqueté « Partiel » PAR CONSTRUCTION**. Confirmé à l'écran : le reçu
   * du décor affiche 5/5, 6/6, 4/4, la commande est « Livré », le reste dû vaut
   * 0,00 € — et la pastille disait « Partiel ».
   * ⚠️ *Un état qui n'a que deux branches pour trois cas n'est pas incomplet :
   * il est FAUX sur le cas manquant, à chaque fois.*
   *
   * 🔴 ET C'EST BIEN LE STATUT DE *RÉCEPTION*, PAS CELUI DE PAIEMENT. Les deux
   * portent le mot « partiel » et vivent à quelques lignes l'une de l'autre ;
   * celui du PAIEMENT (`derivePaymentStatus`) dérive de `reste = total − payé`
   * et il est **juste**. *Deux notions homonymes, une seule cassée — et sans
   * cette distinction on corrige la mauvaise.*
   *
   * ⚠️ LE CRITÈRE EST CELUI DE LA COMMANDE, PAS CELUI DU REÇU. Un reçu ne
   * porte que `quantity_received` : il ne sait rien de ce qui était attendu.
   * C'est la ligne de commande qui porte `quantity` et `received_quantity` —
   * donc « ce reçu a-t-il complété la commande ? » se lit sur la COMMANDE.
   * *Une question posée au mauvais objet n'a pas de réponse ; elle a un
   * défaut.*
   */
  const getReceiptStatus = (receipt) => {
    if (!receipt.items || receipt.items.length === 0) {
      return { status: 'empty', label: t('orders.receiptEmpty') };
    }
    // ⚠️ `remaining_quantity` VIENT DU SERVEUR — on ne resoustrait pas
    // `quantity - received_quantity` ici : la règle vit dans `to_dict()`, et
    // une recopie divergerait le jour où « reçu » cesserait d'être un simple
    // cumul (retours, casse). Repli sur la soustraction seulement si le champ
    // n'est pas servi, pour ne pas rendre l'écran dépendant d'un déploiement.
    const lignes = order?.items || [];
    const resteAReceptionner = lignes.reduce((somme, ligne) => somme + Math.max(
      0,
      ligne.remaining_quantity != null
        ? ligne.remaining_quantity
        : (ligne.quantity || 0) - (ligne.received_quantity || 0),
    ), 0);

    // ⚠️ SANS LIGNES DE COMMANDE, ON NE CONCLUT PAS. Une commande dont les
    // lignes ne sont pas chargées rendrait un reste de 0 — donc « Complet »
    // pour tout le monde. *Un cumul sur une liste vide vaut zéro, et zéro est
    // ici la réponse la plus affirmative qui soit.*
    if (lignes.length === 0) {
      return { status: 'partial', label: t('orders.receiptPartial') };
    }

    return resteAReceptionner === 0
      ? { status: 'complete', label: t('orders.receiptComplete') }
      : { status: 'partial', label: t('orders.receiptPartial') };
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* ═══ UN SEUL PANNEAU, DES FILETS ENTRE LES SECTIONS ═══════════════════
          🔴 IL Y AVAIT TROIS CARTES EMPILÉES — en-tête, totaux, produits —
          séparées par des marges. Trois surfaces disent « trois objets » ; or
          c'est UNE commande, et le regard n'a aucune raison de recommencer sa
          lecture trois fois. Le filet fin sépare sans découper. */}
      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 3, pt: 2.5 }}>
        {/* 🔴 UN SEUL BOUTON COLORÉ, ET IL EST EN DERNIÈRE POSITION. Les autres
            restent disponibles mais en gris : ils ne se disputent plus le regard.
            L'ordre compte autant que la couleur — le geste principal est là où
            l'œil finit sa course. */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<PdfIcon />}
            onClick={() => setPdfOpen(true)}
            size="small"
          >
            {order.pdf_path ? t('orders.pdfViewSent') : t('orders.pdfPreview')}
          </Button>
          {order.status === ORDER_STATUS.EN_ATTENTE_ENVOI && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<MarkEmailReadIcon />}
              onClick={handleMarkSent}
              size="small"
            >
              {t('orders.markAsSent')}
            </Button>
          )}
          {isCancellable(order.status) && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<CancelIcon />}
              onClick={() => setAConfirmer({
                type: 'annulation',
                // ⚠️ CE QUE L'ANNULATION EMPORTE, MESURÉ SUR LA ROUTE : elle
                // pose `status = ANNULE` et **ne touche ni au stock ni aux
                // reçus**. On n'annonce donc PAS « les réceptions seront
                // annulées » — ce serait faux. *On n'annonce que ce qui est
                // servi*, et ici c'est la sortie de la commande du circuit.
                consequences: t('orders.cancelConsequence', {
                  lignes: (order.items || []).length,
                }),
                executer: handleCancel,
              })}
              size="small"
            >
              {t('common.cancel')}
            </Button>
          )}
          {actionPrincipale() && (
            <Button
              variant="contained"
              color={actionPrincipale().couleur}
              startIcon={actionPrincipale().icone}
              onClick={actionPrincipale().action}
              size="small"
            >
              {actionPrincipale().libelle}
            </Button>
          )}
        </Box>
        {/* ═══ LE FIL D'ARIANE — LE NUMÉRO N'EST ÉCRIT QU'UNE FOIS ═════════
            Il l'était DEUX fois : dans le titre d'écran des trois appelants et
            dans cet en-tête. ⚠️ IL N'EST PAS CLIQUABLE, ET C'EST DÉLIBÉRÉ :
            rendre le parent navigable demanderait une prop de rappel, donc un
            changement de COMPORTEMENT — hors d'un lot de mise en forme. Il dit
            où l'on est, il ne prétend pas y ramener. Le bouton « Retour » des
            trois appelants reste le chemin de sortie.
            `contexte` est optionnel : sans lui le parent est « Commandes ». */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {contexte || t('orders.title')}
          </Typography>
          <Typography variant="body2" color="text.disabled">›</Typography>
          <Typography variant="h6" component="h2">
            {numeroAffichable(order.order_number)}
          </Typography>
        </Box>
        </Box>

        <Divider />

        {/* ═══ LA LIGNE DE STATUT — UNE SEULE LIGNE, DES FAITS SÉPARÉS PAR DES FILETS
            Elle était éclatée sur deux niveaux, chaque valeur coiffée de son
            libellé : « Statut » au-dessus de la pastille, « Statut de paiement »
            au-dessus de la sienne. Deux lignes pour trois faits, et une
            demi-largeur vide à droite où logeait le seul fournisseur.
            🔴 LE LIBELLÉ PASSE À CÔTÉ DE LA VALEUR, PAS AU-DESSUS : « Fournisseur
            · Office Solutions » se lit d'un trait, là où un libellé empilé
            oblige l'œil à descendre puis remonter pour le fait suivant. */}
        <Box sx={{ px: 3, py: 1.75, display: 'flex', alignItems: 'center',
                   gap: 2, flexWrap: 'wrap' }}>
          {getStatusChip(order.status)}
          {order.status === ORDER_STATUS.LIVRE && (
            <PastilleStatut statut={getPaymentStatus()} famille="paiement" />
          )}
          {order.send_status && (
            <Tooltip title={order.send_status === 'failed' ? (order.send_error || '') : ''}>
              <Chip
                size="small"
                label={
                  order.send_status === 'sent' ? t('orders.sendStateSent')
                : order.send_status === 'failed' ? t('orders.sendStateFailed')
                : t('orders.sendStateManual')
                }
                color={
                  order.send_status === 'sent' ? 'success'
                : order.send_status === 'failed' ? 'error'
                : 'info'
                }
              />
            </Tooltip>
          )}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {fait(t('orders.supplier'), order.supplier_name || t('suppliers.notSpecified'))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {fait(t('orders.orderedOn'), formaterDateSaisie(order.order_date))}
          {/* ⚠️ « ATTENDU LE », PAS « REÇU LE » — ET C'EST UNE MESURE, PAS UN CHOIX
              D'ÉCRITURE. `PurchaseOrder` porte `order_date`, `expected_delivery_date`,
              `sent_at`, `created_at`, `updated_at` : AUCUNE date de réception. La
              maquette en demandait une ; elle n'existe pas, donc on montre celle
              qui existe. La date du dernier reçu (`PurchaseReceipt.receipt_date`)
              l'approcherait, mais un reçu est une livraison PARTIELLE : sur une
              commande incomplète, l'afficher comme « reçu le » serait faux.
              Le champ ne s'affiche que s'il est renseigné — il est facultatif. */}
          {order.expected_delivery_date && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              {fait(t('orders.expectedOn'), formaterDateSaisie(order.expected_delivery_date))}
            </>
          )}
        </Box>

        <Divider />

      {/* ═══ LE BANDEAU — TROIS CHIFFRES, OU QUATRE AVEC LA TVA ═══════════════
          🔴 LA TVA EST MASQUÉE, PAS AFFICHÉE À ZÉRO. `vat_enabled` est faux par
          défaut : une case « TVA 0,00 » apparaîtrait donc sur la majorité des
          commandes, et une case à zéro n'est pas fausse — elle fait CHERCHER
          pourquoi. Même arbitrage que la colonne « stock après » écartée au lot
          RU3 : on ne montre pas un chiffre exact qui pose une question qui n'a
          pas lieu d'être.
          ⚠️ ET LA DISPOSITION TIENT AUX DEUX TAILLES PARCE QUE LES DEUX DIVISENT
          12 : quatre colonnes de `md={3}`, ou trois de `md={4}`. Une grille MUI
          compte en douzièmes — c'est ce qui rend le masquage sans effet de bord,
          et c'est la raison pour laquelle le nombre de chiffres ne peut pas être
          quelconque. */}
        <Grid container>
          <Grid item xs={12} md={order.vat_enabled ? 3 : 4}>
            <Box sx={CASE}>
              <Typography variant="body2" color="text.secondary">{t('orders.totalHT')}</Typography>
              <Typography variant="h6">
                {formatCurrency(order.total_amount || 0, order.currency || 'EUR')}
              </Typography>
              {/* 🔴 LE TOTAL DES REMISES, ET C'EST UNE GARDE QUI L'A RATTRAPÉ.
                  Le lot RU4-b l'a retiré du tableau des commandes en écrivant
                  « le détail le porte » ; RU4-c a ensuite retiré le pavé du
                  détail qui le portait. Résultat mesuré entre les deux :
                  `order.total_remise` n'était plus affiché NULLE PART dans le
                  front — chaque lot était défendable, leur enchaînement non.
                  ⚠️ C'est une ANNOTATION, pas une cinquième case : le bandeau
                  compte toujours 3 ou 4 douzièmes, et l'écart entre le HT et le
                  total final cesse d'être inexpliqué. */}
              {order.total_remise > 0 && (
                <Typography variant="caption" color="success.main" display="block">
                  −{formatCurrency(order.total_remise, order.currency || 'EUR')} {t('orders.totalDiscounts')}
                </Typography>
              )}
            </Box>
          </Grid>
          {order.vat_enabled && (
            <Grid item xs={12} md={3}>
              <Box sx={{ ...CASE, ...FILET }}>
                <Typography variant="body2" color="text.secondary">
                  {t('purchaseOrderForm.vatAmount')} ({order.vat_rate}%)
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(order.vat_amount || 0, order.currency || 'EUR')}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} md={order.vat_enabled ? 3 : 4}>
            <Box sx={{ ...CASE, ...FILET }}>
              {/* ⚠️ LE LIBELLÉ SUIT `vat_enabled`, ET CE N'EST PAS UN DÉTAIL :
                  « TTC » veut dire *toutes taxes comprises*. Sur une commande
                  sans TVA il n'y a aucune taxe — le montant est le total final,
                  et l'appeler TTC serait faux au sens propre. */}
              <Typography variant="body2" color="text.secondary">
                {order.vat_enabled ? t('orders.totalTTC') : t('orders.finalTotal')}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {formatCurrency(order.total_payable || order.total_final || 0, order.currency || 'EUR')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={order.vat_enabled ? 3 : 4}>
            {/* 🔴 LE RESTE À PAYER EST SUR UN FOND AMBRÉ, pas seulement en texte
                ambre. Une couleur de TEXTE se confond avec une nuance de police
                dans une rangée de chiffres ; une surface teintée désigne la case
                elle-même. Elle passe au vert tendre une fois soldée — même case,
                autre état, et « 0 » reste lisible. */}
            {/* 🔴 LA COULEUR DU TEXTE EST CALCULÉE À PARTIR DU FOND, ET C'EST LE
                SEUL MOYEN QUI TIENNE DANS LES DEUX MODES. Mesuré sur ce fond :
                `warning.main` rend 1,44:1 en clair et 1,12:1 en sombre — le seul
                chiffre qui demande de l'attention était le seul illisible.
                ⚠️ ET LES DEUX CORRECTIONS ÉVIDENTES ÉCHOUENT CHACUNE DANS UN MODE :
                `warning.contrastText` vaut 2,16:1 en clair (il est calculé pour
                `.main`, pas pour `.light`, et `.light` vaut ici `#ff9800` — un
                orange SATURÉ, pas une teinte pâle) ; `text.primary` vaut 1,44:1
                en sombre. `getContrastText(<le fond exact>)` rend 9,74 et 12,13.
                🔴 LA RÈGLE : `X.contrastText` s'appareille avec `X.main`, JAMAIS
                avec une autre nuance. Pour toute autre nuance, la couleur se
                CALCULE depuis la nuance employée. */}
            <Box sx={{ ...CASE, ...FILET,
                       bgcolor: getRemainingAmount() > 0 ? 'warning.light' : 'success.light',
                       color: (theme) => theme.palette.getContrastText(
                         getRemainingAmount() > 0
                           ? theme.palette.warning.light
                           : theme.palette.success.light) }}>
              {/* La hiérarchie vient de la TAILLE et de la GRAISSE, pas d'une
                  seconde couleur : sur une surface teintée, tout écart de teinte
                  se paie en contraste, et le libellé était à 3,52 en clair,
                  1,53 en sombre. Les deux héritent donc de la couleur calculée. */}
              <Typography variant="body2" color="inherit">{t('orders.remainingAmount')}</Typography>
              {/* L'ambre dit « il reste quelque chose à faire ». Soldé, le chiffre
                  passe au vert : c'est la même case qui change d'état, pas une case
                  qui disparaît — on peut donc lire « 0 » et savoir que c'est réglé. */}
              <Typography variant="h6" fontWeight="bold" color="inherit">
                {formatCurrency(getRemainingAmount(), order.currency || 'EUR')}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider />

        {/* ═══ TROIS ONGLETS, AU-DESSUS DU TABLEAU ═══════════════════════
            Les produits étaient une SECTION PERMANENTE et les deux autres vues
            vivaient en dessous : on faisait défiler tout le tableau pour
            atteindre les reçus. 🔴 LES ARTICLES SONT UNE VUE PARMI TROIS, pas
            un socle — c'est ce qui met les trois à la même hauteur et rend le
            panneau lisible sans défilement.
            ⚠️ L'onglet Paiements n'existe que sur une commande LIVRÉE : le
            serveur refuse tout paiement en 409 avant, et un onglet vide
            inviterait à un geste impossible. */}
        <Tabs
          value={ongletCourant}
          onChange={(e, v) => setOngletActif(v)}
          sx={{ px: 2 }}
        >
          <Tab label={t('orders.items')} />
          <Tab label={`${t('orders.deliveryReceipts')} (${receipts.length})`} />
          {ongletPaiementsVisible && (
            <Tab label={`${t('orders.payments')} (${payments.length})`} />
          )}
        </Tabs>

        <Divider />

        {ongletCourant === 0 && (
          <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('products.name')}</TableCell>
                <TableCell align="right">{t('orders.quantity')}</TableCell>
                {/* 🔴 « REÇU » EST COLLÉ À « QTÉ », et c'est la seule position qui
                    permette de lire l'écart d'un coup d'œil. Il était en 7ᵉ colonne,
                    séparé de la quantité commandée par quatre colonnes de montants :
                    comparer 12 et 5 demandait de traverser le tableau. */}
                <TableCell align="right">{t('orders.received')}</TableCell>
                {/* 🔴 « RESTANT » ET « TOTAL HT » SONT RETIRÉS, et la mesure dit
                    pourquoi le second ne coûte rien : `remise_amount` vaut 0 sur
                    les 21 lignes de la base, et `to_dict` retombe sur
                    `total_price` quand `total_with_remise` est vide — les deux
                    colonnes étaient donc IDENTIQUES sur toutes les lignes.
                    « Restant » se déduit d'une soustraction que l'œil fait seul
                    entre deux colonnes voisines. */}
                <TableCell align="right">{t('orders.unitPrice')}</TableCell>
                <TableCell align="center">{t('orders.discount')}</TableCell>
                <TableCell align="right">{t('orders.totalWithDiscount')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {order.items && order.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {item.product_name}
                    </Typography>
                    {item.product_barcode && (
                      <Typography variant="caption" color="text.secondary">
                        {item.product_barcode}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {item.quantity}
                  </TableCell>
                  {/* 🔴 UN CHIFFRE, PAS UNE PASTILLE. Une pastille dit « statut » :
                      elle a une forme fermée, une couleur de fond, et elle se lit
                      comme une étiquette. Or ceci est une QUANTITÉ, qu'on compare
                      à la colonne voisine — et deux nombres ne se comparent pas
                      quand l'un est enfermé dans une capsule et l'autre nu.
                      La couleur reste : elle dit qu'il est arrivé quelque chose. */}
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium"
                                color={item.received_quantity > 0 ? 'success.main' : 'text.disabled'}>
                      {item.received_quantity || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {item.unit_price ? formatCurrency(item.unit_price, order.currency || 'EUR') : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {item.remise_type && item.remise_value ? (
                      <Box>
                        <Typography variant="caption" display="block">
                          {item.remise_type === 'pourcentage' ? '%' : currencySymbol}
                        </Typography>
                        <Typography variant="body2">
                          {item.remise_value}
                        </Typography>
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium" color="primary.main">
                      {item.total_with_remise ? formatCurrency(item.total_with_remise, order.currency || 'EUR') : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
          </>
        )}

        {ongletCourant === 1 && (
          <>
         {error && (
           <Alert severity="error" sx={{ m: 2 }}>
             {error}
           </Alert>
         )}

         {loadingReceipts ? (
           <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
             <CircularProgress />
           </Box>
         ) : receipts.length === 0 ? (
           <Box sx={{ p: 4, textAlign: 'center' }}>
             <Typography variant="body1" color="text.secondary">
               {t('orders.noReceipts')}
             </Typography>
           </Box>
         ) : (
           <Box sx={{ p: 2 }}>
             {receipts.map((receipt) => (
               <Paper key={receipt.id} sx={{ mb: 2, p: 2 }}>
                 {/* En-tête du reçu */}
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                   <Box>
                     <Typography variant="subtitle1" fontWeight="bold">
                       {t('orders.receipt')} #{receipt.receipt_number}
                     </Typography>
                     <Typography variant="caption" color="text.secondary">
                       {formaterHorodatage(receipt.created_at)}
                     </Typography>
                     {receipt.attachment_path && (
                       <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                         <AttachFileIcon fontSize="small" color="primary" />
                         {/* 🔴 UN CLIC, PLUS UN ONGLET. Le lien perdait
                             `X-Stock-ID` — posé par un intercepteur axios que
                             le navigateur n'applique pas à une navigation — et
                             sortait le lecteur de l'écran où il travaillait. */}
                         <Button
                           size="small"
                           startIcon={<AttachFileIcon />}
                           onClick={() => setFichierRegarde({
                             chemin: receipt.attachment_path,
                             titre: t('orders.viewAttachment'),
                             recuId: receipt.id,
                           })}
                         >
                           {t('orders.viewAttachment')}
                         </Button>
                       </Box>
                     )}
                   </Box>
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <Chip
                       label={getReceiptStatus(receipt).label}
                       color={COULEUR_ETAT_RECU[getReceiptStatus(receipt).status]}
                       size="small"
                     />
                     <IconButton
                       size="small"
                       onClick={() => setAConfirmer({
                         type: 'recu',
                         // 🔴 SUPPRIMER UN REÇU RETIRE LA MARCHANDISE DU STOCK
                         // PHYSIQUE — mesuré sur la route : elle appelle
                         // `product.update_stock(quantité, 'subtract')` pour
                         // chaque ligne, décrémente `received_quantity` et
                         // **fait RECULER le statut de la commande**.
                         // *« Supprimer ce reçu » n'apprend rien ; « 3 produits
                         // vont sortir du stock » décide.*
                         consequences: t('orders.deleteReceiptConsequence', {
                           produits: (receipt.items || []).length,
                           unites: (receipt.items || [])
                             .reduce((n, i) => n + (i.quantity_received || 0), 0),
                         }),
                         avertissement: t('orders.deleteReceiptWarning'),
                         executer: () => handleDeleteReceipt(receipt.id),
                       })}
                       color="error"
                     >
                       <DeleteIcon fontSize="small" />
                     </IconButton>
                   </Box>
                 </Box>

                 <Divider sx={{ my: 1 }} />

                 {/* Produits reçus */}
                 {(receipt.items && receipt.items.length > 0) ? (
                   <TableContainer>
                     <Table size="small">
                       <TableHead>
                         <TableRow>
                           <TableCell>{t('products.name')}</TableCell>
                           <TableCell align="right">{t('orders.quantityReceived')}</TableCell>
                           <TableCell align="right">{t('orders.unitPriceShort')}</TableCell>
                           <TableCell align="right">{t('orders.total')}</TableCell>
                         </TableRow>
                       </TableHead>
                       <TableBody>
                         {receipt.items.map((item) => (
                           <TableRow key={item.id}>
                             <TableCell>
                               <Typography variant="body2">
                                 {item.product_name}
                               </Typography>
                               {item.product_barcode && (
                                 <Typography variant="caption" color="text.secondary">
                                   {item.product_barcode}
                                 </Typography>
                               )}
                             </TableCell>
                             <TableCell align="right">
                               {item.quantity_received}
                             </TableCell>
                             <TableCell align="right">
                               {item.unit_price ? formatCurrency(item.unit_price, order.currency || 'EUR') : '-'}
                             </TableCell>
                             <TableCell align="right">
                               <Typography variant="body2" fontWeight="medium">
                                 {item.total_price ? formatCurrency(item.total_price, order.currency || 'EUR') : '-'}
                               </Typography>
                             </TableCell>
                           </TableRow>
                         ))}
                       </TableBody>
                     </Table>
                   </TableContainer>
                 ) : (
                   <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                     {t('orders.noProductsInReceipt')}
                   </Typography>
                 )}

                 {/* Total du reçu */}
                 <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                   <Typography variant="subtitle2" fontWeight="bold">
                     {t('orders.totalReceived')} : {receipt.total_amount ? formatCurrency(receipt.total_amount, order.currency || 'EUR') : '-'}
                   </Typography>
                   {receipt.comment && (
                     <Typography variant="caption" color="text.secondary">
                       {receipt.comment}
                     </Typography>
                   )}
                 </Box>
               </Paper>
             ))}
           </Box>
         )}
          </>
        )}

        {ongletCourant === 2 && (
          <>
         {paymentError && (
           <Alert severity="error" sx={{ m: 2 }}>
             {paymentError}
           </Alert>
         )}

         {loadingPayments ? (
           <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
             <CircularProgress />
           </Box>
         ) : (
           <Box sx={{ p: 2 }}>
             {/* 🔴 IL NE RESTE QU'UN CHIFFRE ICI. Ces trois cartes disaient
                « Total final », « Total payé » et « Montant restant » — et deux
                d'entre elles sont désormais dans le bandeau, à quelques centimètres
                au-dessus. Deux affichages du même montant sur un même écran ne se
                contredisent que le jour où l'un des deux est oublié dans une
                refonte : on n'en garde qu'un. */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t('orders.totalPaid')}
              </Typography>
              <Typography variant="h6" color="success.main" fontWeight="bold">
                {formatCurrency(getTotalPaid(), order.currency || 'EUR')}
              </Typography>
            </Box>

             {payments.length === 0 ? (
               <Box sx={{ p: 4, textAlign: 'center' }}>
                 <PaymentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                 <Typography variant="body1" color="text.secondary">
                   {t('orders.noPayments') || 'Aucun paiement enregistré'}
                 </Typography>
               </Box>
             ) : (
               <TableContainer>
                 <Table>
                   <TableHead>
                     <TableRow>
                       <TableCell>{t('orders.date') || 'Date'}</TableCell>
                       <TableCell>{t('orders.amount') || 'Montant'}</TableCell>
                       <TableCell>{t('orders.paymentType') || 'Type de règlement'}</TableCell>
                       <TableCell>{t('orders.invoice') || 'Facture'}</TableCell>
                       <TableCell>{t('orders.comment') || 'Commentaire'}</TableCell>
                       <TableCell align="right">{t('common.actions')}</TableCell>
                     </TableRow>
                   </TableHead>
                   <TableBody>
                     {payments.map((payment) => (
                       <TableRow key={payment.id}>
                         <TableCell>
                           {formaterDateSaisie(payment.payment_date)}
                         </TableCell>
                         <TableCell>
                           <Typography variant="body2" fontWeight="bold" color="success.main">
                             {formatCurrency(payment.amount, order.currency || 'EUR')}
                           </Typography>
                         </TableCell>
                         <TableCell>
                           <Chip
                             label={libelleTypeReglement(payment.payment_type, t)}
                             size="small"
                             color="primary"
                             variant="outlined"
                           />
                         </TableCell>
                         <TableCell>
                           {/* ⚠️ MÊME VISUALISEUR, SANS `recuId` : une photo de
                               facture de PAIEMENT ne se remplace pas — la route
                               n'existe que pour les reçus. L'absence de
                               l'identifiant suffit à masquer le bouton. */}
                           {payment.invoice_photo_path ? (
                             <Button
                               size="small"
                               startIcon={<ImageIcon />}
                               onClick={() => setFichierRegarde({
                                 chemin: payment.invoice_photo_path,
                                 titre: t('orders.viewInvoice') || 'Voir la facture',
                               })}
                             >
                               {t('orders.viewInvoice') || 'Voir la facture'}
                             </Button>
                           ) : (
                             <Typography variant="caption" color="text.secondary">
                               -
                             </Typography>
                           )}
                         </TableCell>
                         <TableCell>
                           <Typography variant="body2" color="text.secondary">
                             {payment.comment || '-'}
                           </Typography>
                         </TableCell>
                         <TableCell align="right">
                           <IconButton
                             size="small"
                             onClick={() => setAConfirmer({
                               type: 'paiement',
                               // 🔴 LE RESTE DÛ REMONTE D'AUTANT — et c'est le
                               // seul chiffre qui décide. La route supprime
                               // aussi **la photo de facture** du disque, sans
                               // retour possible : l'avertissement le dit,
                               // parce que c'est la partie qu'on ne peut pas
                               // refaire en resaisissant le paiement.
                               consequences: t('orders.deletePaymentConsequence', {
                                 montant: formatCurrency(payment.amount, order.currency || 'EUR'),
                               }),
                               avertissement: payment.invoice_photo_path
                                 ? t('orders.deletePaymentPhotoWarning') : undefined,
                               executer: () => handleDeletePayment(payment.id),
                             })}
                             color="error"
                           >
                             <DeleteIcon fontSize="small" />
                           </IconButton>
                         </TableCell>
                       </TableRow>
                     ))}
                   </TableBody>
                 </Table>
               </TableContainer>
             )}
           </Box>
         )}
          </>
        )}

       {/* ═══ LE COMMENTAIRE, EN BAS ET SUR TOUTE LA LARGEUR ═══════════════
           Il vivait dans l'en-tête, à droite, dans une demi-colonne partagée avec
           le fournisseur : un texte de trois phrases y était coupé.
           🔴 `whiteSpace: 'pre-wrap'` — SANS LUI, LES RETOURS À LA LIGNE SAISIS
           DISPARAISSENT. HTML réduit toute suite d'espaces et de sauts à un seul
           espace : un commentaire écrit en trois paragraphes se rend en un pavé,
           et personne ne voit que l'information a été aplatie — elle est toujours
           là, elle est juste devenue illisible. */}
       {order.comment && (
         <>
           <Divider />
           <Box sx={{ p: 3 }}>
             <Typography variant="subtitle2" color="text.secondary" gutterBottom>
               {t('orders.comment')}
             </Typography>
             <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
               {order.comment}
             </Typography>
           </Box>
         </>
       )}
      </Paper>

       <DialogueAjoutPaiement
         commande={order}
         ouvert={paymentDialogOpen}
         onFermer={handleClosePaymentDialog}
         onEnregistre={enregistrerPaiement}
         showSnackbar={showSnackbar}
       />

      <ReceiptFormDialog
        order={order}
        open={receiptDialogOpen}
        onClose={() => setReceiptDialogOpen(false)}
        onSuccess={() => {
          loadReceipts();
          onOrderUpdated && onOrderUpdated();
        }}
        showSnackbar={showSnackbar}
      />
      {/* 🔴 UNE SEULE ANNONCE POUR LES TROIS GESTES. Le titre, la question et
          le libellé du bouton viennent du TYPE ; les conséquences viennent de
          l'objet cliqué. *Un bouton qui dit « OK » fait relire la question ;
          un bouton qui dit ce qu'il fait se lit seul.* */}
      <DialogueConfirmation
        ouvert={Boolean(aConfirmer)}
        titre={aConfirmer ? t(`orders.confirm${
          { annulation: 'CancelTitle', recu: 'DeleteReceiptTitle',
            paiement: 'DeletePaymentTitle' }[aConfirmer.type]}`) : ''}
        question={aConfirmer ? t(`orders.confirm${
          { annulation: 'CancelQuestion', recu: 'DeleteReceiptQuestion',
            paiement: 'DeletePaymentQuestion' }[aConfirmer.type]}`) : ''}
        consequences={aConfirmer?.consequences}
        avertissement={aConfirmer?.avertissement}
        libelleConfirmer={aConfirmer ? t(
          aConfirmer.type === 'annulation' ? 'orders.cancelOrder' : 'common.delete',
        ) : ''}
        enCours={confirmationEnCours}
        onConfirmer={executerConfirmation}
        onFermer={() => setAConfirmer(null)}
      />

      <DocumentViewerModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        url={`/purchase-orders/${order.id}/pdf`}
        title={order.pdf_path ? t('orders.pdfViewSent') : t('orders.pdfPreview')}
        filename={`bon-de-commande-${order.order_number}.pdf`}
      />

      {/* 🔴 LE MÊME COMPOSANT POUR LES PIÈCES JOINTES — pièce jointe de reçu et
          photo de facture de paiement. Les deux ouvraient un ONGLET ; il y avait
          donc DEUX façons de regarder un fichier dans le même écran, plus une
          troisième ailleurs (l'image produit, qui n'était pas cliquable). */}
      <DocumentViewerModal
        open={Boolean(fichierRegarde)}
        onClose={() => { setFichierRegarde(null); setErreurRemplacement(null); }}
        url={fichierRegarde ? `/files/${fichierRegarde.chemin}` : null}
        title={fichierRegarde?.titre || ''}
        filename={fichierRegarde?.chemin}
        mimeType={typeDuFichier(fichierRegarde?.chemin).mimeType}
        estImage={typeDuFichier(fichierRegarde?.chemin).estImage}
        actions={fichierRegarde?.recuId ? (
          <>
            {erreurRemplacement && (
              <Typography variant="caption" color="error" sx={{ mr: 'auto' }}>
                {erreurRemplacement}
              </Typography>
            )}
            {/* ⚠️ « REMPLACER », PAS « SUPPRIMER PUIS AJOUTER ». La route écrase
                le fichier et efface l'ancien dans la même requête : découper le
                geste en deux laisserait un reçu SANS pièce jointe si le second
                échoue — et c'est justement ce qu'on vient de vouloir garder. */}
            <Button component="label" size="small" startIcon={<AttachFileIcon />}
                    disabled={remplacementEnCours}>
              {remplacementEnCours ? t('common.loading') : t('orders.replaceAttachment')}
              <input type="file" hidden accept="image/*,application/pdf"
                     onChange={remplacerPieceJointe} />
            </Button>
          </>
        ) : null}
      />
     </Box>
   );
 };

export default OrderDetailView;
